import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import supplierItemService from "@plannel/services/data/supplier-item-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import { Autocomplete, TextField } from "@mui/material";
import locationService from "@plannel/services/data/location-service";
import supplierService from "@plannel/services/data/supplier-service";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import ResponseUtil from "../utils/response-util";
import dateUtils from "@plannel/utils/date-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { httpStatusCode, responseCode } = ResponseUtil;
const ACTIVE_FLAG = "activeFlg";
const ITEM_CD = "itemCd";
const LOCATION_NAME = "locName";
const PLANT = "PLANT";
const SupplierItemMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const [supplierSearch, setSupplierSearch] = useState(reduxViewState?.supplierSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [supplierItemData, setSupplierItemData] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationCdValueFormatter = (location) => {
    const formatter = location?.value?.locCd;
    return formatter ?? "";
  }

  const locationNmValueFormatter = (location) => {
    const formatter = location?.value?.name;
    return formatter ?? "";
  }

  const supplierCdValueFormatter = (supplier) => {
    const formatter = supplier?.value?.supplierCd;
    return formatter ?? "";
  }

  const supplierNmValueFormatter = (supplier) => {
    const formatter = supplier?.value?.name;
    return formatter ?? "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const onCellValueChanged = (params) => {
    const { column, node, data } = params;
    const editedColId = column.getColId();
    const api = gridRef.current.api;

    if ((editedColId === ACTIVE_FLAG || editedColId === ITEM_CD || editedColId === LOCATION_NAME) && node.isSelected()) {  
      const editLoc = data.location.locCd;
      const editItem = data?.item?.itemCd ?? data?.itemCd;

      api.forEachNode((rowNode) => {
        if(node.rowIndex !== rowNode.rowIndex) {
          const rowNodeData = rowNode.data;
          const rowLoc = rowNodeData?.location.locCd;
          const rowItem = rowNodeData?.item?.itemCd ?? rowNodeData.itemCd;
          if (rowLoc && rowItem && editLoc === rowLoc && editItem === rowItem) {
            rowNode.setDataValue(ACTIVE_FLAG, false);
          }
        }
      });

    }

    DefaultGridSetting({}).onCellValueChanged(params);
    api.stopEditing();
  }

  const columnDefs = [
    {
      headerName: "locCd", field: "location", colId: "locCd",
      type: ['nonEditableColumn'],
      valueFormatter: locationCdValueFormatter,
      cellClass: 'stringType',
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
      width: 150, filterType: "string"
    },
    {
      headerName: "locName", field: "location", colId: "locName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: locationNmValueFormatter,
      cellEditorParams: {
        values: locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150, 
      filterType: "string",
    },
    { headerName: "itemCd", field: "itemCd", colId: "itemCd", width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "itemName", colId: "itemName", width: 150, type: ['nonEditableColumn'], filterType: "string" },
    {
      headerName: "supplierCd", field: "supplier", colId: "supplierCd",
      type: ['nonEditableColumn'],
      cellClass: 'stringType',
      valueFormatter: supplierCdValueFormatter,
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "supplierCd", isDescending),
      width: 150, filterType: "string"
    },
    {
      headerName: "supplierName", field: "supplier", colId: "supplierName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: supplierNmValueFormatter,
      cellEditorParams: {
        values: suppliers
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150, 
      filterType: "string",
    },
    {
      headerName: "avgLtDays", field: "avgLtDays", type: ["numberColumn", "rightAligned"],
      cellEditor: NumericEditor,
      cellEditorParams: {
        setSnackInfo,
        min: 0,
      },
      width: 80, filterType: "number"
    },
    { headerName: ACTIVE_FLAG, field: ACTIVE_FLAG, type: ["booleanColumn"], width: 80, filterType: "boolean" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 180, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 180, filterType: "string" }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (supplierSearch) {
      searchFilters.supplierCd = supplierSearch.supplierCd;
    }

    if (locationSearch) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveLocation = () => {
    locationService.getLookup({ isLocationMaster: true })
      .then(response => {
        const { results } = response.data;
        const location = [];
        for (const result of results) {
          if (result.locType === PLANT && result.activeFlg) {
            location.push(result);
          }
        }
        setLocations(location);
      })
      .catch(e => {
        debug(e);
      })
  }

  const retrieveSupplier = () => {
    supplierService.getAll()
      .then(response => {
        const { results } = response.data;
        setSuppliers(results);
      })
      .catch(e => {
        debug(e);
      })
  }

  const retrieveSupplierItem = () => {
    const params = getRequestParams();
    
    supplierItemService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;

        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }


        setSupplierItemData(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { supplierSearch, locationSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    retrieveLocation();
    retrieveSupplier();

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removeSupplierItem = () => {
    const supplierIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {    
        supplierIds.push(data.id)  
      }
    );

    if (supplierIds.length > 0) {
      supplierItemService.remove(supplierIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveSupplierItem();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveSupplierItem = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
    
      supplierItemService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .then(() => {
          retrieveSupplierItem();
        })
        .catch(e => {
          const status = e.response?.status;
          const errorDatas = e.response?.data;

          if(status === httpStatusCode.BadRequest) {
            for (const errorCode of Object.keys(errorDatas)) {
              if (errorCode === responseCode.NOT_FOUND_ITEM) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistItemCodes", { itemCodes: errorDatas[errorCode] }) });
              } else if (errorCode === responseCode.INVALID_ITEM_TYPE) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ItemTypeCheck", { itemCodes: errorDatas[errorCode] }) });
              } else {
                setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
              }
              break;
            }
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveSupplierItem();
    } else if (action === "DELETE") {
      removeSupplierItem();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSupplierItem();
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      location: {locCd: null, name: null},
      item: {itemCd: null, name: null},
      supplier: {supplierCd: null, name: null},
      avgLtDays: null,
      activeFlg: true,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!isEmpty(locations) || !isEmpty(suppliers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, suppliers]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveSupplierItem();
    }

    return () => {
      setSupplierItemData([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setSupplierItemData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, supplierSearch, locationSearch]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <Autocomplete
          options={suppliers}
          getOptionLabel={option => option.name}
          value={supplierSearch}
          onChange={(_, newValue) => setSupplierSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.supplierCd === value.supplierCd}
          size="small"
          sx={{ mr: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("supplier")} />
          )}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ mr: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} sx={{ mr: 1 }} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={supplierItemService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="supplier-item-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={supplierItemData}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
              onCellValueChanged={onCellValueChanged}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SupplierItemMaster);